import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Calendar, Plus, Trash2, Clock, MapPin, Bell } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Appointments = ({ user, onLogout }) => {
  const [appointments, setAppointments] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    case_id: "none",
    title: "",
    appointment_type: "court_session",
    date: "",
    location: "",
    notes: ""
  });

  useEffect(() => {
    fetchAppointments();
    fetchCases();
    requestNotificationPermission();
  }, []);

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  };

  const scheduleNotification = (appointment) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const appointmentDate = new Date(appointment.date);
      const now = new Date();
      const timeDiff = appointmentDate.getTime() - now.getTime();
      
      // إشعار قبل ساعة
      const oneHourBefore = timeDiff - (60 * 60 * 1000);
      if (oneHourBefore > 0) {
        setTimeout(() => {
          new Notification('تذكير بموعد قادم', {
            body: `لديك موعد: ${appointment.title} خلال ساعة`,
            icon: '/logo.jpeg'
          });
        }, oneHourBefore);
      }

      // إشعار قبل 15 دقيقة
      const fifteenMinBefore = timeDiff - (15 * 60 * 1000);
      if (fifteenMinBefore > 0) {
        setTimeout(() => {
          new Notification('تذكير بموعد قادم', {
            body: `لديك موعد: ${appointment.title} خلال 15 دقيقة`,
            icon: '/logo.jpeg'
          });
        }, fifteenMinBefore);
      }
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data);
      // جدولة الإشعارات للمواعيد القادمة
      response.data.forEach(apt => {
        if (new Date(apt.date) > new Date()) {
          scheduleNotification(apt);
        }
      });
    } catch (error) {
      toast.error("خطأ في تحميل المواعيد");
    } finally {
      setLoading(false);
    }
  };

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases`);
      setCases(response.data);
    } catch (error) {
      console.error("Error fetching cases:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        case_id: formData.case_id === "none" ? null : formData.case_id,
        date: new Date(formData.date).toISOString()
      };
      
      const response = await axios.post(`${API}/appointments`, payload);
      toast.success("تم إضافة الموعد بنجاح");
      
      // جدولة الإشعار للموعد الجديد
      scheduleNotification(response.data);
      
      setDialogOpen(false);
      resetForm();
      fetchAppointments();
    } catch (error) {
      toast.error("خطأ في إضافة الموعد");
    }
  };

  const resetForm = () => {
    setFormData({
      case_id: "none",
      title: "",
      appointment_type: "court_session",
      date: "",
      location: "",
      notes: ""
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الموعد؟")) return;
    
    try {
      await axios.delete(`${API}/appointments/${id}`);
      toast.success("تم حذف الموعد");
      fetchAppointments();
    } catch (error) {
      toast.error("خطأ في حذف الموعد");
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      court_session: "جلسة محكمة",
      client_meeting: "لقاء عميل",
      deadline: "موعد نهائي",
      other: "أخرى"
    };
    return types[type] || type;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ar-IQ", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const isUpcoming = (dateString) => {
    return new Date(dateString) > new Date();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8" data-testid="appointments-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">إدارة المواعيد</h1>
            <p className="text-lg text-slate-600">تنظيم ومتابعة جلساتك ومواعيدك</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-appointment-button">
                <Plus className="w-5 h-5" />
                موعد جديد
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="add-appointment-dialog">
              <DialogHeader>
                <DialogTitle>إضافة موعد جديد</DialogTitle>
                <DialogDescription>أضف موعد جديد وسيتم تذكيرك قبل موعده</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">عنوان الموعد *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                    data-testid="appointment-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="appointment_type">نوع الموعد *</Label>
                  <Select
                    value={formData.appointment_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, appointment_type: value }))}
                  >
                    <SelectTrigger id="appointment_type" data-testid="appointment-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="court_session">جلسة محكمة</SelectItem>
                      <SelectItem value="client_meeting">لقاء عميل</SelectItem>
                      <SelectItem value="deadline">موعد نهائي</SelectItem>
                      <SelectItem value="other">أخرى</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="case_id">ربط بقضية (اختياري)</Label>
                  <Select
                    value={formData.case_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, case_id: value }))}
                  >
                    <SelectTrigger id="case_id" data-testid="appointment-case-select">
                      <SelectValue placeholder="اختر قضية" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">لا توجد</SelectItem>
                      {cases.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date">التاريخ والوقت *</Label>
                  <Input
                    id="date"
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    required
                    data-testid="appointment-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">المكان</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    data-testid="appointment-location-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">ملاحظات</Label>
                  <Textarea
                    id="notes"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    data-testid="appointment-notes-input"
                  />
                </div>

                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Bell className="w-4 h-4" />
                  <span>سيتم تذكيرك قبل الموعد بساعة وقبله بـ 15 دقيقة</span>
                </div>

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    إلغاء
                  </Button>
                  <Button type="submit" data-testid="submit-appointment-button">حفظ الموعد</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Appointments List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-slate-200 rounded mb-2"></div>
                  <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-16 bg-slate-200 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : appointments.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Calendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 text-lg">لا توجد مواعيد حالياً. ابدأ بإضافة موعد!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {appointments.map((appointment) => (
              <Card
                key={appointment.id}
                className={`hover:shadow-lg transition-shadow ${
                  isUpcoming(appointment.date) ? "border-l-4 border-l-blue-600" : ""
                }`}
                data-testid={`appointment-card-${appointment.id}`}
              >
                <CardHeader>
                  <CardTitle className="text-xl mb-2">{appointment.title}</CardTitle>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="badge badge-blue">{getTypeLabel(appointment.appointment_type)}</span>
                    {isUpcoming(appointment.date) && (
                      <span className="flex items-center gap-1 text-green-600">
                        <Bell className="w-3 h-3" />
                        <span className="text-xs">قادم</span>
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Clock className="w-4 h-4" />
                      <span>{formatDate(appointment.date)}</span>
                    </div>
                    {appointment.location && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="w-4 h-4" />
                        <span>{appointment.location}</span>
                      </div>
                    )}
                    {appointment.notes && (
                      <div className="pt-2">
                        <p className="text-slate-600 text-xs line-clamp-2">{appointment.notes}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(appointment.id)}
                      className="gap-2"
                      data-testid={`delete-appointment-${appointment.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      حذف
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Appointments;